import React, {useState, useEffect} from 'react';
import {Image} from 'antd';
import {InstagramOutlined, PictureOutlined, VideoCameraOutlined} from '@ant-design/icons';

interface InstagramThumbnailProps {
    url: string;
    thumbnailS3Key?: string; // S3 key (for reference)
    thumbnailPublicURL?: string; // S3 public URL (preferred)
    mediaType: string;
    alt?: string;
    size?: number;
}

/**
 * Custom thumbnail component for Instagram content that handles problematic CDN URLs
 *
 * Instagram story URLs often have complex authentication parameters that cause them to fail
 * when loaded in standard img tags. This component detects these problematic URLs and
 * shows appropriate fallback icons instead.
 */
const InstagramThumbnail: React.FC<InstagramThumbnailProps> = ({
                                                                   url,
                                                                   thumbnailS3Key,
                                                                   thumbnailPublicURL,
                                                                   mediaType,
                                                                   alt,
                                                                   size = 60
                                                               }) => {
    const [imageError, setImageError] = useState(false);

    // useEffect(() => {
    //     console.log('InstagramThumbnail props:', {
    //         url,
    //         thumbnailS3Key,
    //         thumbnailPublicURL,
    //         mediaType,
    //         alt,
    //         size
    //     });
    // }, [url, thumbnailS3Key, thumbnailPublicURL, mediaType, alt, size]);

    const getMediaIcon = () => {
        const iconSize = Math.floor(size * 0.4);
        switch (mediaType.toLowerCase()) {
            case 'story':
                return <InstagramOutlined style={{fontSize: iconSize, color: '#E4405F'}}/>;
            case 'post':
                return <PictureOutlined style={{fontSize: iconSize, color: '#1890ff'}}/>;
            case 'reel':
                return <VideoCameraOutlined style={{fontSize: iconSize, color: '#722ed1'}}/>;
            default:
                return <PictureOutlined style={{fontSize: iconSize, color: '#666'}}/>;
        }
    };

    const fallbackStyle = {
        width: size,
        height: size,
        display: 'flex' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        border: '1px solid #d9d9d9'
    };

    // If we've had an error loading the image, show fallback
    if (imageError) {
        return (
            <div style={fallbackStyle}>
                {getMediaIcon()}
            </div>
        );
    }

    // Determine best thumbnail source (prioritize S3 URL over original URL)
    const imageSrc = thumbnailPublicURL || url;

    return (
        <Image
            width={size}
            height={size}
            src={imageSrc}
            alt={alt}
            style={{objectFit: 'cover', borderRadius: 4}}
            onError={() => setImageError(true)}
            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1IiByeD0iNCIvPgo8Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIxMiIgZmlsbD0iI0Q5RDlEOSIvPgo8cGF0aCBkPSJNMjQgMjZMMzYgMzJMMjQgMzhaIiBmaWxsPSIjNjY2Ii8+Cjwvc3ZnPgo="
        />
    );
};

export default InstagramThumbnail;